import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Award, Users, Building2, Flame, Truck, Settings, Wrench, ArrowRight } from 'lucide-react';
import HeroSlideshow from '../components/HeroSlideshow';
import ClientsMarquee from '../components/ClientsMarquee';
import { stats, products, whyChooseUs } from '../mock';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Slideshow */}
      <HeroSlideshow />

      {/* Stats Section — premium counter cards */}
      <section className="bg-white py-14 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-slate-100 border border-slate-100 rounded-md overflow-hidden">
            {stats.map((stat, index) => (
              <div
                key={index}
                className={`bg-white text-center px-6 py-8 flex flex-col items-center justify-center ${
                  stats.length % 2 === 1 && index === stats.length - 1
                    ? 'col-span-2 md:col-span-1'
                    : ''
                }`}
              >
                <div className="font-display text-4xl md:text-5xl font-extrabold text-red-600 mb-2 tracking-tight">{stat.value}</div>
                <div className="text-sm font-medium uppercase tracking-wider text-slate-700">{stat.label}</div>
                {stat.subtitle && (
                  <div className="text-xs text-slate-500 mt-2 italic">{stat.subtitle}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="relative py-24 text-white overflow-hidden" style={{ backgroundColor: '#1F1F1F' }}>
        <div className="absolute inset-0 opacity-[0.05]" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)',
          backgroundSize: '48px 48px'
        }}></div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="eyebrow" style={{ color: '#f4a8b4' }}>Established 1927</span>
          <h2 className="font-display text-3xl md:text-5xl font-extrabold text-white mt-3 mb-6 text-balance">
            A Century of Fire & Safety Engineering
          </h2>
          <div className="w-16 h-[3px] bg-red-600 mx-auto mb-8"></div>
          <div className="text-slate-200 leading-relaxed space-y-5 text-base md:text-lg">
            <p>
              Bhartiya Vehicles & Engineering Co. Ltd. stands as a testament to excellence in fire and safety engineering, with a remarkable legacy spanning nearly a century since 1927. We are a leading force in manufacturing world-class fire fighting vehicles, rescue equipment, and comprehensive fire protection systems.
            </p>
            <p>
              Our commitment to innovation, reliability, and the &lsquo;Make in India&rsquo; vision has made us the trusted partner of India&apos;s most prestigious organizations including the Government of India, Ministry of Defence, Indian Air Force, Indian Navy, and major airports across the nation. As an ISO 9001:2015 certified company, we deliver one-stop solutions for all fire and safety needs, backed by state-of-the-art manufacturing facilities, advanced technology, and a team of 500+ skilled professionals.
            </p>
          </div>
          <div className="mt-10">
            <Link to="/about">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 h-12 rounded-sm">
                Learn More About Us
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <span className="eyebrow">Our Strengths</span>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-slate-900 mt-3 mb-4 text-balance">
              Engineering Excellence Built on Trust
            </h2>
            <p className="text-base md:text-lg text-slate-600">
              Nearly a century of excellence, innovation, and unwavering commitment to fire and safety engineering.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="bg-white p-8 group transition-colors hover:bg-slate-50 flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-red-600/10 rounded-md flex items-center justify-center mb-6 transition-colors group-hover:bg-red-600">
                  {index === 0 && <Award className="h-6 w-6 text-red-600 transition-colors group-hover:text-white" />}
                  {index === 1 && <Shield className="h-6 w-6 text-red-600 transition-colors group-hover:text-white" />}
                  {index === 2 && <Flame className="h-6 w-6 text-red-600 transition-colors group-hover:text-white" />}
                  {index === 3 && <Settings className="h-6 w-6 text-red-600 transition-colors group-hover:text-white" />}
                  {index === 4 && <Building2 className="h-6 w-6 text-red-600 transition-colors group-hover:text-white" />}
                  {index === 5 && <Users className="h-6 w-6 text-red-600 transition-colors group-hover:text-white" />}
                </div>
                <h3 className="font-display text-xl font-semibold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Product Categories Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <span className="eyebrow">Our Portfolio</span>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-slate-900 mt-3 mb-4 text-balance">
              Featured Products
            </h2>
            <p className="text-base md:text-lg text-slate-600">
              Comprehensive range of fire fighting vehicles and protection systems.
            </p>
            <Link to="/products" className="hidden md:inline-flex items-center text-sm font-semibold text-red-600 hover:text-red-700 group mt-6">
              View all products
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                category: "Firefighting Vehicles",
                description: "ARFFV, Aerial Vehicles, DFT, Emergency Rescue Tender, and Forest Firefighting Vehicles for every emergency need",
                icon: Truck,
                color: "from-red-600 to-red-700",
                image: "/images/e6cwgbbu_ChatGPT_Image_May_29_2026_03_30_43_PM.png",
                link: "/products?category=ARFFV"
              },
              {
                category: "Equipment",
                description: "Firefighting Equipment, Firefighter's Equipment, Pumps and Accessories",
                icon: Shield,
                color: "from-slate-700 to-slate-900",
                image: "/images/c87p50y3_IMG_6894.WEBP",
                link: "/products?category=Firefighting+Equipment"
              },
              {
                category: "Fire Protection Systems",
                description: "Complete fire safety, detection, and suppression systems",
                icon: Settings,
                color: "from-red-700 to-slate-900",
                image: "/images/zzwg7mxd_Picture18.jpg",
                link: "/products?category=Fire+Protection+Systems"
              },
              {
                category: "Firefighting Robots",
                description: "Advanced robotic solutions for high-risk fire suppression and rescue operations",
                icon: Flame,
                color: "from-slate-900 to-red-600",
                image: "/images/hector.png",
                link: "/products?category=Firefighting+Robots"
              },
              {
                category: "Rescue Tools",
                description: "Professional hydraulic & rescue equipment for emergency operations",
                icon: Wrench,
                color: "from-slate-800 to-red-700",
                image: "/images/24jofadk_IMG_6895.jpg",
                link: "/products?category=Rescue+Tools"
              }
            ].map((cat, index) => {
              const Icon = cat.icon;

              return (
                <Link
                  key={index}
                  to={cat.link}
                  className="group block"
                >
                  <Card className="overflow-hidden bg-white border border-slate-200 rounded-md shadow-none hover:shadow-xl hover:-translate-y-1 hover:border-slate-300 transition-all duration-300 h-full flex flex-col">
                    <div className={`h-56 bg-gradient-to-br ${cat.color} flex items-center justify-center relative overflow-hidden`}>
                      {cat.image ? (
                        <img
                          src={cat.image}
                          alt={cat.category}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <>
                          <div className="absolute inset-0 opacity-10" style={{
                            backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 50%, white 1px, transparent 1px)',
                            backgroundSize: '40px 40px'
                          }}></div>
                          <Icon className="h-24 w-24 text-white group-hover:scale-110 transition-transform duration-300" />
                        </>
                      )}
                    </div>
                    <CardContent className="p-6 flex-1 flex flex-col">
                      <div className="mb-3">
                        <h3 className="font-display text-lg md:text-xl font-bold text-slate-900 leading-tight">{cat.category}</h3>
                      </div>
                      <p className="text-slate-600 mb-5 text-sm leading-relaxed">{cat.description}</p>
                      <div className="flex items-center text-red-600 font-semibold text-sm mt-auto pt-3 border-t border-slate-100 group-hover:border-red-200 transition-colors">
                        <span>Explore Range</span>
                        <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-14 md:hidden">
            <Link to="/products">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 rounded-sm">
                View All Products
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section className="py-20 bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <div className="text-center max-w-3xl mx-auto">
            <span className="eyebrow">Trusted Partners</span>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-slate-900 mt-3 mb-4 text-balance">
              Trusted by Leading Organizations
            </h2>
            <p className="text-base md:text-lg text-slate-600">
              Serving India's most prestigious government and defense institutions.
            </p>
          </div>
        </div>
        <ClientsMarquee />
      </section>

      {/* CTA Section — premium dark with accent */}
      <section className="relative py-24 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)',
          backgroundSize: '48px 48px'
        }}></div>
        <div className="absolute left-0 top-0 h-1 w-full bg-red-600"></div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="eyebrow" style={{ color: '#f4a8b4' }}>Get In Touch</span>
          <h2 className="font-display text-3xl md:text-5xl font-extrabold mb-5 mt-3 text-balance">
            Ready to Enhance Your Fire Safety?
          </h2>
          <p className="text-base md:text-lg mb-10 max-w-2xl mx-auto text-slate-300">
            Speak to our engineering team about customized fire and safety solutions built for your operation.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-10 h-14 text-base rounded-sm">
              Contact Us Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
